import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IBursaAPIBidCert, defaultValue } from 'app/shared/model/bursa-api-bid-cert.model';

const initialState: EntityState<IBursaAPIBidCert> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/bursa-api-bid-certs';

// Actions

export const getEntities = createAsyncThunk('bursaAPIBidCert/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IBursaAPIBidCert[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'bursaAPIBidCert/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IBursaAPIBidCert>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'bursaAPIBidCert/create_entity',
  async (entity: IBursaAPIBidCert, thunkAPI) => {
    const result = await axios.post<IBursaAPIBidCert>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'bursaAPIBidCert/update_entity',
  async (entity: IBursaAPIBidCert, thunkAPI) => {
    const result = await axios.put<IBursaAPIBidCert>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'bursaAPIBidCert/partial_update_entity',
  async (entity: IBursaAPIBidCert, thunkAPI) => {
    const result = await axios.patch<IBursaAPIBidCert>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'bursaAPIBidCert/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IBursaAPIBidCert>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getEntityByEcertNo = createAsyncThunk(
  'bursaAPIBidCert/fetch_entity_bid_cert_by_ecert_no',
  async (ecertNo: string) => {
    const requestUrl = `${apiUrl}/by-ecert-no/${ecertNo}`;
    return axios.get<IBursaAPIBidCert>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

// slice

export const BursaAPIBidCertSlice = createEntitySlice({
  name: 'bursaAPIBidCert',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getEntityByEcertNo.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntityByEcertNo), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = BursaAPIBidCertSlice.actions;

// Reducer
export default BursaAPIBidCertSlice.reducer;
